"use client";

import { linkHoverColor } from "@faire/deprecated-retailer-visitor-shared/components/Header/Colors";
import { HeaderLinkBase } from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderLinkBase";
import { AccessibilityLinkCss } from "@faire/deprecated-retailer-visitor-shared/lib/AccessibilityLink";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useUserStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useUserStore";
import { Core } from "@faire/design-tokens";
import { useEnv } from "@faire/web/common/env/client";
import { Button } from "@faire/web/slate-deprecated/Button";
import { VerticalLine } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import * as React from "react";
import styled, { css } from "styled-components";

export const HeaderIconContainer = styled.div`
  width: 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 32px;
  }
`;

export const HeaderItemCss = css`
  ${TypographyStyles({ as: undefined })};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 0 12px;
  cursor: pointer;
  transition: color 0.1s linear;
  white-space: nowrap;

  ${AccessibilityLinkCss}

  &:hover {
    color: ${linkHoverColor};
  }

  ${responsiveMediaQueries.mobile} {
    height: ${() => (isLoggedInRetailer() ? "30px" : "50px")};
    ${() => (isLoggedInRetailer() ? "margin-top: 12px" : undefined)};
  }
`;

export const HeaderLink = styled(HeaderLinkBase)`
  ${HeaderItemCss};
`;

export const HeaderAnchor = styled(Anchor)`
  ${HeaderItemCss};
`;

export const HeaderButton = styled(Button).attrs({ variant: "primary" })`
  height: 36px;
  padding: 0 20px;
  margin: 0 12px;
  white-space: nowrap;
  width: max-content;

  ${responsiveMediaQueries.mobile} {
    ${TypographyStyles({ variant: "labelSansRegular", color: "white" })}
    margin-left: 0;
    margin-right: 12px;
    padding: 0 10px;
    height: 32px;
  }
`;

export const HeaderLinkButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => {
  const { className, ...rest } = props;
  return (
    <button
      className={cn(
        className,
        "relative flex items-center justify-center px-4",
        "text-core-text-default",
        "hover:text-core-text-hover",
        isLoggedInRetailer() ? "mt-3 h-[30px]" : "h-[50px]",
        "md:h-[60px]",
        "cursor-pointer"
      )}
      {...rest}
    />
  );
};

export const HeaderLine = styled(VerticalLine).attrs({
  height: "24px",
  color: Core.border.subdued,
  margin: "0 15px",
})``;

export const HeaderDropdown = styled.div<{
  $right?: string;
  $left?: string;
  $width?: string;
  $maxHeight?: string;
  $noBoxShadow?: boolean;
}>`
  position: absolute;
  width: ${(props) => (props.$width ? props.$width : "250px")};
  ${(props) =>
    props.$maxHeight
      ? css`
          max-height: ${props.$maxHeight};
          overflow: auto;
        `
      : ""}
  background: white;
  top: 60px;
  right: ${(props) => (props.$right ? props.$right : "5px")};
  ${(props) =>
    props.$left
      ? css`
          left: ${props.$left};
        `
      : ""};
  box-shadow: ${(props) =>
    props.$noBoxShadow ? "none" : "0 1px 20px rgba(0, 0, 0, 0.1)"};
  z-index: 1;
`;

const HeaderContainerBase = styled.header<{
  $zIndex: number;
  $isMinimal?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${Core.surface.inverse};
  z-index: ${(props) => props.$zIndex};
  border-bottom: 1px solid ${Core.border.muted};
  transition: background 0.1s linear;
  position: relative;
  flex: 0 0 auto;

  @media print {
    display: none;
  }
`;

interface IHeaderContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  $isMinimal?: boolean;
}

export const HeaderContainer = ({
  ref,
  ...props
}: IHeaderContainerProps & {
  ref?: React.Ref<HTMLElement>;
}) => {
  const { isImpersonating, isEmployee } = useUserStore();
  const { envName } = useEnv();

  const className = cn(
    props.className,
    isImpersonating ? `box-border !shadow-[0_2px_0_0_#00FF00]` : "",
    isEmployee ? `box-border !shadow-[0_2px_0_0_#0000FF]` : "",
    envName === "local" ? `box-border !shadow-[0_2px_0_0_orange]` : ""
  );

  return (
    <HeaderContainerBase
      {...props}
      className={className}
      $zIndex={301}
      ref={ref}
    >
      {props.children}
    </HeaderContainerBase>
  );
};
